/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated),
 * and the public website: / (landing), /guide (the Codex), /news (the
 * Chronicle), /news/:slug (one entry), /legal/:slug. The old static-page URLs
 * (the .html Codex/Chronicle/article documents) are redirected client-side.
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';

// PWA: register the service worker (production only — the dev server serves
// modules the worker must never intercept). This is what upgrades the manifest
// from "bookmark" to a real installable app (CU-86cb58yuh item 16).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => { /* install offer just won't appear */ });
  });
}
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { attachVerifyRedirect } from './engine/verify-redirect';
import { attachAuthBackdrop, attachAuthLogo } from './engine/auth-backdrop';
import { addBackToLanding } from './engine/back-pill';
import { shouldRouteInApp } from './engine/spa-link';
import { legacyRedirect, matchSiteRoute } from './engine/site/site-kit';
import { captureReferralCode } from './engine/game/referral-api';
import { installIdleLogout, sessionExpiredWhileAway, clearSeen } from './engine/game/idle-logout';
import { initLocale } from './engine/i18n';
import { installPrompt } from './engine/pwa-install';
import { syncCookieNotice } from './engine/cookie-notice';
import { setRates } from './engine/i18n/money';
import { fetchRates } from './engine/game/i18n-api';

// Referral links (`?ref=CODE`) can land on any pre-auth screen (landing,
// /login, /register) — capture + strip before the core auth UI ever renders;
// the actual claim happens later, once a session exists (see HeroScreen).
captureReferralCode();

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

/** Combine several teardown fns into one. */
const combine = (...fns: Array<(() => void) | null | undefined>) => () => fns.forEach((f) => f?.());

/**
 * Everything the game hangs on a core auth screen: the ambient loop behind it
 * and the brand lockup centred on the art pane. Both ride the one hook core
 * gives us (`.bg-auth-gradient`), and every auth route gets both — the six
 * screens are one surface and must not drift apart.
 */
const authChrome = (host: HTMLElement) => combine(attachAuthBackdrop(host), attachAuthLogo(host));

/**
 * The token was just spent server-side, so any cached user still says
 * emailVerified:false — /play would bounce it straight to /check-email.
 * Re-bootstrap first so the store reflects the verification.
 */
function addVerifyRedirect(): () => void {
  return attachVerifyRedirect(document, async () => {
    const user = await bootstrapSession();
    navigate(user?.emailVerified ? '/play' : '/login');
  });
}

/** Bumped on every render so a lazy page chunk that lands late never mounts over a newer route. */
let renderSeq = 0;

async function render() {
  teardown?.();
  teardown = null;
  const seq = ++renderSeq;

  // Legacy static-page URLs → their SPA routes, without a history entry.
  const legacy = legacyRedirect(location.pathname, location.search);
  if (legacy) history.replaceState(null, '', legacy);

  const path = location.pathname;
  // The one-time cookie note rides every public route, never the game/admin.
  syncCookieNotice(path);

  // The public website pages: open to everyone, lazy-loaded so the game
  // bundle does not carry them.
  const site = matchSiteRoute(path);
  if (site) {
    void import('./engine/site/mount-site').then(({ mountSitePage }) => {
      if (seq !== renderSeq) return;
      teardown = mountSitePage(app, site);
    }).catch((err) => { console.error('[site] page chunk failed to load', err); });
    return;
  }

  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    navigate('/play');
  };

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderLogin(app, afterAuth), authChrome(app), addBackToLanding(() => navigate('/')));
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = combine(renderRegister(app, afterAuth), authChrome(app), addBackToLanding(() => navigate('/')));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = combine(renderForgotPassword(app), authChrome(app), addBackToLanding(() => navigate('/')));
      break;
    case '/reset-password':
      teardown = combine(renderResetPassword(app, params.get('token') ?? '', () => navigate('/login')), authChrome(app));
      break;
    case '/verify-email':
      teardown = combine(renderVerifyEmail(app, params.get('token') ?? ''), authChrome(app), addVerifyRedirect());
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = combine(renderCheckEmail(app, user.email, () => { navigate('/login'); }), authChrome(app));
      break;
    case '/play':
    case '/':
      if (!user) {
        // First-time / logged-out visitors see the marketing landing at the root;
        // its "Play Free" CTAs hand off to the login screen.
        if (path === '/') { const { renderLanding } = await import('./engine/landing/landing-screen'); teardown = renderLanding(app, () => navigate('/login')); break; }
        navigate('/login');
        return;
      }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      {
        // AWAIT the logout before routing: `render()` reads authStore, and with
        // the store still holding a user the /login case bounced straight back
        // to /play (remounting the whole game) until the POST happened to land.
        const endSession = () => { clearSeen(); void logout().then(() => navigate('/login')); };
        // Product rule: 10 minutes without input ends the session (and with it,
        // any chance of coming back to hours-stale in-memory game state).
        const { mountGame } = await import('./engine/bootstrap');
        teardown = combine(mountGame(app, user, endSession), installIdleLogout(endSession));
      }
      break;
    // The separate admin app that used to live behind /admin is gone; the
    // in-game panel is the only admin. Keep the old URL working rather than
    // bouncing a bookmarked /admin to the login screen.
    case '/admin':
      navigate('/admin-panel');
      return;
    case '/admin-panel/sandbox':
    case '/admin-panel':
      if (!user) { navigate('/login'); return; }
      if (user.role !== 'ADMIN' && user.role !== 'KYC_ADMIN') { navigate('/play'); return; }
      const { mountAdmin } = await import('./engine/game/admin-page');
      teardown = mountAdmin(app, user);
      break;
    default:
      // /legal/:slug is a site page now (matchSiteRoute above), not a route here.
      navigate('/login');
  }
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || !shouldRouteInApp(a)) return;
  e.preventDefault();
  navigate(a.pathname + a.search + a.hash);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  // Locale before the first paint, so a returning German visitor never sees a
  // flash of English. Awaited alongside the session rather than after it: the
  // pre-auth screens need the catalogue too, and neither depends on the other.
  // Display FX rates ride along; a failure there only means prices render in
  // the charge currency (see i18n/money.ts), so it must not block boot.
  await Promise.all([
    bootstrapSession(),
    initLocale(),
    fetchRates().then(setRates).catch(() => {}),
  ]);

  // The refresh cookie outlives the tab by days, so a session restored above is
  // only good if the player was actually here recently. A tab closed (or a
  // laptop asleep) for the idle timeout is a logged-out player — the same ten
  // minutes rule 1 enforces while the page is open. QA 86cb9yd3p item 2.
  if (authStore.get().user && sessionExpiredWhileAway()) {
    clearSeen();
    await logout();
  }

  render();

  // The install offer, armed once for the whole app rather than per screen: it
  // outlives routing (login → landing → /play) and no browser shows one by
  // itself any more. Mounted after the catalogue loads so its copy is already
  // in the player's tongue. See engine/pwa-install.ts.
  installPrompt();

  // NOTE: deliberately NO global re-render on locale change. The Vue tree
  // repaints reactively, and the landing refills its own copy in place (see
  // landing/lang-pick.ts) precisely so that switching tongue does not reset
  // scroll position or restart the ember/snow animations. A blanket render()
  // here would undo both.

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/login');
  });
})();

// NOTE: no beforeunload teardown. Browsers fire beforeunload for navigations
// that never happen (mailto: links hand off to the mail client and stay on the
// page) — tearing down there left a blank, dead SPA. On a real unload the
// browser discards the page anyway; nothing needs manual cleanup.
